import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChefHat, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const KitchenLoginPage = () => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("kitchen_token");
    if (token) {
      axios.get(`${API}/kitchen/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        if (res.data.valid) navigate("/kjokkenet", { replace: true });
        else setCheckingAuth(false);
      }).catch(() => {
        localStorage.removeItem("kitchen_token");
        setCheckingAuth(false);
      });
    } else {
      setCheckingAuth(false);
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) { setError("Skriv inn PIN-kode"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/kitchen/login`, { pin });
      localStorage.setItem("kitchen_token", res.data.access_token);
      navigate("/kjokkenet", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Feil PIN-kode");
      setPin("");
    }
    setLoading(false);
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}>
        <div className="w-16 h-16 border-4 rounded-full animate-spin" style={{ borderColor: '#d4af37', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm"
      >
        <div
          className="rounded-2xl shadow-2xl p-8"
          style={{ background: 'rgba(45, 24, 16, 0.95)', border: '2px solid #d4af37' }}
        >
          <div className="text-center mb-8">
            <div
              className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: 'rgba(212, 175, 55, 0.15)', border: '2px solid #d4af37' }}
            >
              <ChefHat className="h-10 w-10" style={{ color: '#d4af37' }} />
            </div>
            <h1 className="text-2xl font-bold" style={{ color: '#d4af37' }} data-testid="kitchen-login-title">
              Kjokken
            </h1>
            <p className="text-sm mt-1" style={{ color: '#b8a080' }}>
              Skriv inn PIN-kode for a logge inn
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: '#d4af37' }}>
                <Lock className="h-4 w-4 inline mr-1" /> PIN-kode
              </label>
              <div className="relative">
                <input
                  type={showPin ? "text" : "password"}
                  value={pin}
                  onChange={(e) => { setPin(e.target.value); setError(""); }}
                  placeholder="****"
                  className="w-full px-4 py-3 rounded-xl text-center text-2xl tracking-[0.5em] font-mono focus:outline-none transition-colors"
                  style={{
                    background: 'rgba(212, 175, 55, 0.1)',
                    border: `2px solid ${error ? '#dc2626' : '#d4af37'}`,
                    color: '#f5e6d3'
                  }}
                  autoFocus
                  data-testid="kitchen-pin-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: '#d4af37' }}
                >
                  {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg text-center text-sm" style={{ background: 'rgba(220, 38, 38, 0.15)', color: '#ef4444', border: '1px solid rgba(220, 38, 38, 0.3)' }} data-testid="kitchen-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-bold text-lg transition-all hover:scale-[1.02] disabled:opacity-50"
              style={{ background: '#d4af37', color: '#2d1810' }}
              data-testid="kitchen-login-submit"
            >
              {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : "Logg inn"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default KitchenLoginPage;
